import { AbstractControl, ValidationErrors, AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged, switchMap, startWith, take } from 'rxjs/operators';
import { validatePrompt$ } from '@vendasta/protos/ai_assistants/v1alpha1/goal-service';
import { ElevenLabsVoiceRejectionReason } from '@vendasta/protos/voice/v1alpha1';
import { validateElevenLabsVoice$ } from '@vendasta/protos/voice/v1alpha1/voice-service';

export function urlValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return null;
    }
    return { invalidHttpUrl: true };
  } catch {
    return { invalidHttpUrl: true };
  }
}

// Create async validator for template validation
export function createTemplateValidator(): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const content = control.value;

    // Don't validate empty content
    if (!content || content.trim() === '') {
      return of(null);
    }

    // `pristine` rather than `dirty`, which AiPromptControl redefines as "differs from what was loaded" and so
    // reports for a value nobody has touched. Writers that apply a prompt programmatically mark it themselves.
    if (!control.touched && control.pristine) {
      return of(null);
    }

    // Use the control's valueChanges to get proper debouncing across keystrokes
    return control.valueChanges.pipe(
      startWith(content), // Include the current value
      debounceTime(500), // Debounce to avoid excessive API calls
      distinctUntilChanged(),
      switchMap((value) => {
        // Skip validation for empty content
        if (!value || value.trim() === '') {
          return of(null);
        }

        return validatePrompt$({ content: value }).pipe(
          map((response) => (response.isValid ? null : { templateSyntax: true })),
          catchError(() => of(null)),
        );
      }),
      take(1),
    );
  };
}

// Async validator for a custom ElevenLabs voice ID. Probes Voice's validation endpoint directly so
// the error surfaces as the user types, rather than only when the assistant is saved.
export function createElevenLabsVoiceValidator(): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const voiceId = control.value;

    if (!voiceId || (!control.touched && !control.dirty)) {
      return of(null);
    }

    return control.valueChanges.pipe(
      startWith(voiceId),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((value) => {
        if (!value) {
          return of(null);
        }

        return validateElevenLabsVoice$({ voiceId: value }).pipe(
          map((response) => {
            switch (response.rejectionReason) {
              case ElevenLabsVoiceRejectionReason.ELEVEN_LABS_VOICE_REJECTION_REASON_NOT_FOUND:
                return { notFound: true };
              case ElevenLabsVoiceRejectionReason.ELEVEN_LABS_VOICE_REJECTION_REASON_SURCHARGE:
                return { surcharge: true };
              default:
                // NONE means usable; INVALID means the backend couldn't decide, so fail open and let
                // the authoritative write-path validation catch it on save.
                return null;
            }
          }),
          catchError(() => of(null)),
        );
      }),
      take(1),
    );
  };
}

/**
 * Cross-field validator that requires url and method fields when generatesAnswer is false
 */
export function requireUrlAndMethodWhenNotGeneratingAnswer(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    if (!formGroup || typeof formGroup.get !== 'function') {
      return null;
    }

    const generatesAnswer = formGroup.get('generatesAnswer')?.value;
    const noOp = formGroup.get('noOp')?.value;
    const url = formGroup.get('url');
    const method = formGroup.get('method');

    // If generatesAnswer is true, no validation needed for url/method
    if (generatesAnswer || noOp) {
      return null;
    }

    const errors: ValidationErrors = {};

    // Check if URL is required and missing
    if (!url?.value) {
      errors['urlRequired'] = true;
    } else {
      // Also validate URL format if present
      const urlValidationResult = urlValidator(url);
      if (urlValidationResult) {
        errors['urlInvalid'] = true;
      }
    }

    // Check if method is required and missing
    if (!method?.value) {
      errors['methodRequired'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}
